import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence, useScroll } from "framer-motion";
import { Menu, X, ArrowUpRight, Sun, Moon, ChevronDown } from "lucide-react";
import { LogoFull } from "./Logo";
import { useAnnouncementOffset } from "./AnnouncementBar";

const serviceLinks = [
  { to: "/services/brand-website", label: "Brand & Website", testid: "nav-service-brand-website" },
  { to: "/services/automation", label: "WhatsApp & AI Automation", testid: "nav-service-automation" },
  { to: "/services/sops", label: "SOPs & Operations", testid: "nav-service-sops" },
];

const links = [
  { to: "/pricing", label: "Pricing", testid: "nav-pricing-link" },
  { to: "/work", label: "Work", testid: "nav-work-link" },
  { to: "/quikpik", label: "Quikpik", testid: "nav-quikpik-link" },
  { to: "/about", label: "About", testid: "nav-about-link" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [theme, setTheme] = useState("dark");
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const logoClicks = useRef([]);
  const { scrollYProgress } = useScroll();
  const navOffset = useAnnouncementOffset();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const t = localStorage.getItem("allcov-theme") === "light" ? "light" : "dark";
    setTheme(t);
    document.documentElement.classList.toggle("light", t === "light");
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  const toggleTheme = () => {
    const next = theme === "dark" ? "light" : "dark";
    setTheme(next);
    localStorage.setItem("allcov-theme", next);
    document.documentElement.classList.toggle("light", next === "light");
  };

  const onLogoClick = (e) => {
    const now = Date.now();
    logoClicks.current = [...logoClicks.current.filter((t) => now - t < 2000), now];
    if (logoClicks.current.length >= 5) {
      logoClicks.current = [];
      e.preventDefault();
      navigate("/admin");
    }
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed left-0 right-0 top-0 z-50 border-b transition-colors duration-500 ${
        scrolled || open ? "border-line backdrop-blur-xl" : "border-transparent bg-transparent"
      }`}
      style={{ top: navOffset, ...(scrolled || open ? { backgroundColor: "var(--navbg)" } : {}) }}
    >
      <motion.div
        style={{ scaleX: scrollYProgress }}
        className="absolute bottom-0 left-0 h-[2px] w-full origin-left bg-myrtle"
        aria-hidden="true"
      />
      <div className="relative mx-auto flex max-w-7xl items-center justify-between px-6 py-5 lg:px-10">
        <Link to="/" onClick={onLogoClick} data-testid="nav-logo" aria-label="Allcov home">
          <LogoFull />
        </Link>
        <nav className="absolute left-1/2 hidden -translate-x-1/2 items-center gap-8 lg:flex" aria-label="Primary">
          <div className="group relative">
            <button
              data-testid="nav-services-dropdown"
              className="flex items-center gap-1.5 text-sm uppercase tracking-[0.15em] text-mut transition-colors duration-300 hover:text-pop"
            >
              Services
              <ChevronDown size={14} className="transition-transform duration-300 group-hover:rotate-180" />
            </button>
            <div className="invisible absolute left-1/2 top-full -translate-x-1/2 pt-4 opacity-0 transition-all duration-200 group-hover:visible group-hover:opacity-100">
              <div className="w-64 border border-line bg-surface py-2 shadow-xl shadow-black/30">
                {serviceLinks.map((s) => (
                  <Link
                    key={s.to}
                    to={s.to}
                    data-testid={s.testid}
                    className="block px-5 py-3 text-sm text-mut transition-colors duration-200 hover:bg-myrtle/10 hover:text-txt"
                  >
                    {s.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={l.testid}
              className="text-sm uppercase tracking-[0.15em] text-mut transition-colors duration-300 hover:text-pop"
            >
              {l.label}
            </Link>
          ))}
        </nav>
        <div className="hidden items-center gap-4 lg:flex">
          <button
            onClick={toggleTheme}
            data-testid="theme-toggle-button"
            aria-label="Toggle light and dark mode"
            className="flex h-10 w-10 items-center justify-center border border-line text-mut transition-colors duration-300 hover:border-myrtle hover:text-pop"
          >
            <AnimatePresence mode="wait" initial={false}>
              <motion.span
                key={theme}
                initial={{ rotate: -90, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: 90, opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="flex"
              >
                {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
              </motion.span>
            </AnimatePresence>
          </button>
          <Link
            to="/contact"
            data-testid="nav-talk-to-us-button"
            className="group flex items-center gap-2 bg-myrtle px-6 py-3 text-xs uppercase tracking-[0.15em] text-white transition-colors duration-300 hover:bg-myrtle-light"
          >
            Talk to Us
            <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </Link>
        </div>
        <div className="flex items-center gap-3 lg:hidden">
          <button
            onClick={toggleTheme}
            data-testid="theme-toggle-button-mobile"
            aria-label="Toggle light and dark mode"
            className="flex h-10 w-10 items-center justify-center border border-line text-mut"
          >
            {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
          </button>
          <button
            data-testid="nav-mobile-menu-button"
            onClick={() => setOpen(!open)}
            className="text-txt"
            aria-label="Toggle menu"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden border-t border-line bg-base lg:hidden"
            aria-label="Mobile"
          >
            <div className="flex flex-col gap-5 px-6 py-8">
              <p className="text-xs uppercase tracking-[0.25em] text-faint">Services</p>
              {serviceLinks.map((s) => (
                <Link key={s.to} to={s.to} data-testid={`mobile-${s.testid}`} className="pl-4 font-batangas text-2xl text-txt">
                  {s.label}
                </Link>
              ))}
              <div className="my-1 h-px bg-line" />
              {links.map((l) => (
                <Link key={l.to} to={l.to} data-testid={`mobile-${l.testid}`} className="font-batangas text-2xl text-txt">
                  {l.label}
                </Link>
              ))}
              <Link
                to="/contact"
                data-testid="mobile-nav-talk-to-us-button"
                className="mt-2 inline-flex w-fit items-center gap-2 bg-myrtle px-8 py-4 text-xs uppercase tracking-[0.15em] text-white"
              >
                Talk to Us <ArrowUpRight size={14} />
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
